import React, { useEffect, useState } from 'react';
import { Table, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Toast from '../../../shared/Toast';

const Url = process.env.REACT_APP_MAIN_URL;

const StandingsTable = ({ divisionId, teamId, token, refreshTrigger }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    if (divisionId) {
      fetchStandings();
    }
  }, [divisionId, refreshTrigger]); // Add refreshTrigger to dependencies

  const fetchStandings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Url}api/standings/division/${divisionId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      
      if (response.data.success) {
        setStandings(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standings:', error);
      Toast.error('Failed to load standings');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedStandings = () => {
    if (!sortConfig.key) {
      return standings;
    }

    return [...standings].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle string comparison for team names
      if (sortConfig.key === 'teamName') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      }

      // Handle numeric comparison
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="ms-1 text-muted" style={{ opacity: 0.3 }}>⇅</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <span className="ms-1">↑</span>
    ) : (
      <span className="ms-1">↓</span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading standings...</p>
      </div>
    );
  }

  if (standings.length === 0) {
    return (
      <div className="text-center py-5">
        <h5 className="text-muted">No standings available yet</h5>
        <p className="text-muted">Games need to be completed to generate standings</p>
      </div>
    );
  }

  const sortedStandings = getSortedStandings();

  return (
    <div className="standings-table-container">
      <h4 className="mb-3 fw-bold">Division Standings</h4>
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive>
          <thead className="bg-dark text-white">
            <tr>
              <th className="text-center">#</th>
              <th 
                onClick={() => handleSort('teamName')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                Team {getSortIndicator('teamName')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('wins')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                W {getSortIndicator('wins')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('losses')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                L {getSortIndicator('losses')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('ties')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                T {getSortIndicator('ties')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('winningPercentage')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                PCT {getSortIndicator('winningPercentage')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('gamesBack')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                GB {getSortIndicator('gamesBack')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('runsScored')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                RS {getSortIndicator('runsScored')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('runsAllowed')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                RA {getSortIndicator('runsAllowed')}
              </th>
              <th 
                className="text-center" 
                onClick={() => handleSort('runDifferential')} 
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Click to sort"
              >
                DIFF {getSortIndicator('runDifferential')}
              </th>
              <th className="text-center">HOME</th>
              <th className="text-center">AWAY</th>
            </tr>
          </thead>
          <tbody>
            {sortedStandings.map((standing, index) => (
              <tr
                key={standing.standingId}
                className={
                  teamId && standing.teamId === teamId
                    ? 'table-primary fw-bold'
                    : ''
                }
              >
                <td className="text-center">{index + 1}</td>
                <td>
                  {standing.teamName}
                  {teamId && standing.teamId === teamId && (
                    <span className="badge bg-primary ms-2">Your Team</span>
                  )}
                </td>
                <td className="text-center">{standing.wins}</td>
                <td className="text-center">{standing.losses}</td>
                <td className="text-center">{standing.ties}</td>
                <td className="text-center">
                  {standing.winningPercentage.toFixed(3)}
                </td>
                <td className="text-center">
                  {standing.gamesBack === 0 ? '-' : standing.gamesBack.toFixed(1)}
                </td>
                <td className="text-center">{standing.runsScored}</td>
                <td className="text-center">{standing.runsAllowed}</td>
                <td className="text-center">
                  <span
                    className={
                      standing.runDifferential > 0
                        ? 'text-success fw-bold'
                        : standing.runDifferential < 0
                        ? 'text-danger fw-bold'
                        : ''
                    }
                  >
                    {standing.runDifferential > 0 ? '+' : ''}
                    {standing.runDifferential}
                  </span>
                </td>
                <td className="text-center">{standing.homeRecord}</td>
                <td className="text-center">{standing.awayRecord}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default StandingsTable;
